import type { ComponentChildren } from 'preact';
import { AppIcon } from './AppIcon';

/** A field's label row: the label on the left and its error, with an icon, on the right, right-aligned with the
 * control. When the two don't fit on one line the error moves onto its own line, still right-aligned, and wraps.
 * A required field left empty (`missing`) shows its error only as a red Required, with the icon, to save the line the
 * message would take; screen readers still hear the message.
 * The error is outside the label; the caller links it to the control with aria-describedby while there is one. Its
 * region is always present, so a new error is announced once and not again while it stays. */
export function FieldHeader({ label, htmlFor, labelId, labelClass, error, errorId, missing }: {
  label: ComponentChildren; htmlFor?: string; labelId?: string; labelClass?: string; error?: string | null; errorId: string; missing?: boolean;
}) {
  const className = `field-header-label${labelClass ? ` ${labelClass}` : ''}`;
  return <div class={`field-header${missing && error ? ' missing' : ''}`}>
    {htmlFor ? <label class={className} for={htmlFor} id={labelId}>{label}</label> : <span class={className} id={labelId}>{label}</span>}
    <span class="field-header-error" id={errorId} aria-live="polite">{error && <><AppIcon name="alert" /><span>{error}</span></>}</span>
  </div>;
}

/** The ids that describe a control: its error first while it has one, then any help. */
export function describedBy(error: string | null | undefined, errorId: string, ...help: Array<string | false | null | undefined>) {
  return [error && errorId, ...help].filter(Boolean).join(' ') || undefined;
}

/** After a failed Continue, focus the first invalid control and bring it into view. */
export function focusFirstInvalid(root: ParentNode | null | undefined) {
  const field = root?.querySelector<HTMLElement>('[aria-invalid="true"]');
  field?.focus();
  field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

/** An error with no label row to sit in, such as one about a whole question or list: beneath it, with the icon. */
export function FieldError({ id, role, children }: { id?: string; role?: 'alert'; children: ComponentChildren }) {
  return <span class="error-message" id={id} role={role}><AppIcon name="alert" /><span>{children}</span></span>;
}
